import React, { useEffect, useState } from 'react';
import AppShell from '../../components/layout/AppShell';
import { getLoNavigation } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { getSignedUrl } from '../../lib/supabase';
import { ArrowDownTrayIcon, ArrowPathIcon, DocumentTextIcon, EnvelopeIcon, PhotoIcon } from '@heroicons/react/24/outline';

interface ResourceItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  url: string | null;
  signedUrl?: string | null;
  type: 'download' | 'view';
}

const MyResources: React.FC = () => {
  const { loanOfficer, refreshLoanOfficer } = useAuth();
  const navigation = getLoNavigation(loanOfficer?.location_id ?? undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  // Refresh LO data when page loads to get latest resources
  useEffect(() => {
    refreshLoanOfficer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch signed URLs for private storage resources
  useEffect(() => {
    const fetchSignedUrls = async () => {
      const urls: Record<string, string> = {};

      // Get signed URL for headshot
      if (loanOfficer?.headshot) {
        const signed = await getSignedUrl(loanOfficer.headshot);
        if (signed) urls['headshot'] = signed;
      }

      // Get signed URL for email signature
      if (loanOfficer?.email_signature_url) {
        const signed = await getSignedUrl(loanOfficer.email_signature_url);
        if (signed) urls['email_signature'] = signed;
      }

      setSignedUrls(urls);
    };

    fetchSignedUrls();
  }, [loanOfficer?.headshot, loanOfficer?.email_signature_url]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshLoanOfficer();
    // Clear signed URLs to force refetch
    setSignedUrls({});
    setRefreshing(false);
  };

  // Build resources list based on LO data
  const resources: ResourceItem[] = [
    {
      id: 'email_signature',
      title: 'Email Signature',
      description: 'Your personalized email signature for use in Outlook, Gmail, and other email clients.',
      icon: EnvelopeIcon,
      url: loanOfficer?.email_signature_url || null,
      type: 'download',
    },
    {
      id: 'headshot',
      title: 'Professional Headshot',
      description: 'Your professional headshot for marketing materials and profiles.',
      icon: PhotoIcon,
      url: loanOfficer?.headshot || null,
      type: 'view',
    },
  ];

  const availableResources = resources.filter((r) => r.url);
  const pendingResources = resources.filter((r) => !r.url);

  return (
    <AppShell
      title="My Resources"
      description="Download your personalized materials and resources."
      nav={navigation}
    >
      <div className="space-y-8">
        {/* Available Resources */}
        <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-900">Available Downloads</h2>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-600 text-sm font-medium hover:bg-slate-200 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {availableResources.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <DocumentTextIcon className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm">No resources available yet.</p>
              <p className="text-xs mt-1">Your admin will upload resources as they become ready.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {availableResources.map((resource) => {
                const Icon = resource.icon;
                return (
                  <div
                    key={resource.id}
                    className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{resource.title}</h3>
                        <p className="text-xs text-slate-500 mt-1">{resource.description}</p>
                        {signedUrls[resource.id] ? (
                          <a
                            href={signedUrls[resource.id]}
                            download={resource.type === 'download'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            {resource.type === 'download' ? 'Download' : 'View'}
                          </a>
                        ) : (
                          <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl bg-slate-300 text-slate-500 text-sm font-medium">
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                            Loading...
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Pending Resources */}
        {pendingResources.length > 0 && (
          <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Coming Soon</h2>
            <p className="text-sm text-slate-500 mb-4">
              These resources are being prepared by your admin team.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              {pendingResources.map((resource) => {
                const Icon = resource.icon;
                return (
                  <div
                    key={resource.id}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-5 opacity-75"
                  >
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-700">{resource.title}</h3>
                        <p className="text-xs text-slate-400 mt-1">{resource.description}</p>
                        <div className="mt-3 px-4 py-2 rounded-xl bg-slate-200 text-slate-500 text-sm font-medium inline-block">
                          Pending
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Help Section */}
        <section className="rounded-3xl border border-slate-100 bg-[#f9fbff] p-6">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
            Need Help?
          </h3>
          <p className="text-sm text-slate-600">
            If you're missing a resource or have questions about using your materials,
            please contact your onboarding coordinator.
          </p>
        </section>
      </div>
    </AppShell>
  );
};

export default MyResources;
